import React, { useRef, useEffect, useState } from 'react'
import { Icon } from '@iconify/react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { classroomDashboard, classroomVisibility } from '../../../services/adminService';
import ClassroomLiveClasses from './ClassroomLiveClasses';
import ClassroomAssessments from './ClassroomAssessments';
import ClassroomAssignments from './ClassroomAssignments';
import ClassroomCommunity from '../../user/classroom/Cummunity';
import ClassroomRecorded from './ClassroomRecorded';

import { usePermissions } from '../../../context/PermissionsContext';
import { encodeData, decodeData } from '../../../utils/encodeAndEncode';

function ClassroomDashboard() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const { classroomId } = useParams();
  const decodedClassroomId = decodeData(classroomId);
  // console.log('decodedClassroomId', decodedClassroomId);

  const [activeTab, setActiveTab] = useState('live');
  const [classroomData, setClassroomData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [groupData, setGroupData] = useState(null);
  const [classroomName, setClassroomName] = useState('');
  const [isTrainersExpanded, setIsTrainersExpanded] = useState(false);
  const [isTabSettingsOpen, setIsTabSettingsOpen] = useState(false);
  const [tabVisibility, setTabVisibility] = useState({
    assignments: true,
    assessments: true,
    live: true,
    recorded: true,
    community: true
  });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside dropdown or on the button
      if (dropdownRef.current?.contains(event.target) || buttonRef.current?.contains(event.target)) {
        return;
      }
      // Close if clicking outside
      if (isTabSettingsOpen) {
        setIsTabSettingsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isTabSettingsOpen]);

  // Mock function to simulate API call for tab visibility update
  const updateTabVisibility = async (e, tabName, isVisible) => {
    e.stopPropagation();

    try {
      // Create new state
      const newState = {
        ...tabVisibility,
        [tabName]: isVisible
      };

      // Generate payload for API
      const is_visibility = [
        {
          tab_name: "assignment",
          is_visible: newState.assignments.toString()
        },
        {
          tab_name: "assessment",
          is_visible: newState.assessments.toString()
        },
        {
          tab_name: "liveclass",
          is_visible: newState.live.toString()
        },
        {
          tab_name: "recorded",
          is_visible: newState.recorded.toString()
        },
        {
          tab_name: "community",
          is_visible: newState.community.toString()
        }
      ];

      // Prepare API payload
      const apiPayload = {
        classroom_id: parseInt(decodedClassroomId),
        is_visibility: is_visibility
      };

      // Update local state first (optimistic update)
      setTabVisibility(newState);

      // Call the API
      const response = await classroomVisibility(apiPayload);

      if (response.success) {
        toast.success('Tab visibility updated successfully');
      } else {
        // Revert state if API call fails
        setTabVisibility(tabVisibility);
        toast.error('Failed to update tab visibility');
      }
    } catch (error) {
      // Revert state if API call fails
      setTabVisibility(tabVisibility);
      console.error('Error updating tab visibility:', error);
      toast.error('Failed to update tab visibility');
    }
  };

  // Tab settings component
  const TabSettings = () => (
    <div
      ref={dropdownRef}
      className="absolute bg-white shadow-sm rounded-lg p-3"
      style={{ right: '0', top: '100%', zIndex: 1000, minWidth: '200px' }}
      onClick={(e) => e.stopPropagation()}
    >
      <h6 className="mb-3 font-semibold">Tab Visibility</h6>
      <div className="flex flex-col gap-2">
        <label className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={tabVisibility.assignments}
            onChange={(e) => updateTabVisibility(e, 'assignments', !tabVisibility.assignments)}
          />
          <span style={{ color: tabVisibility.assignments ? 'inherit' : '#666' }}>
            Assignments
          </span>
        </label>
        <label className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={tabVisibility.assessments}
            onChange={(e) => updateTabVisibility(e, 'assessments', !tabVisibility.assessments)}
          />
          <span style={{ color: tabVisibility.assessments ? 'inherit' : '#666' }}>
            Assessments
          </span>
        </label>
        <label className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={tabVisibility.live}
            onChange={(e) => updateTabVisibility(e, 'live', !tabVisibility.live)}
          />
          <span style={{ color: tabVisibility.live ? 'inherit' : '#666' }}>
            Live Classes
          </span>
        </label>
        {/* Hidden: Resources tab
        <label className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={tabVisibility.recorded}
            onChange={(e) => updateTabVisibility(e, 'recorded', !tabVisibility.recorded)}
          />
          <span style={{ color: tabVisibility.recorded ? 'inherit' : '#666' }}>
            Resources
          </span>
        </label>
        */}
        {/* Hidden: Community tab
        <label className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={tabVisibility.community}
            onChange={(e) => updateTabVisibility(e, 'community', !tabVisibility.community)}
          />
          <span style={{ color: tabVisibility.community ? 'inherit' : '#666' }}>
            Community
          </span>
        </label>
        */}
      </div>
    </div>
  );

  const fetchDashboardData = async () => {
    try {
      const response = await classroomDashboard({ classroom_id: decodedClassroomId });

      console.log('Dashboard data---------:', response);
      // console.log('Classroom name:', response?.cls_name);

      if (response) {
        setDashboardData(response.data);
        setTrainers(response.data || []);
        setGroupData(response.group);
        if (response.cls_name) {
          setClassroomName(response.cls_name);
        }

        // Update tab visibility from API response
        if (response.is_visibility && Array.isArray(response.is_visibility)) {
          const newTabVisibility = {
            assignments: response.is_visibility.find(tab => tab.tab_name === 'assignment')?.is_visible === 'true',
            assessments: response.is_visibility.find(tab => tab.tab_name === 'assessment')?.is_visible === 'true',
            live: response.is_visibility.find(tab => tab.tab_name === 'liveclass')?.is_visible === 'true',
            recorded: response.is_visibility.find(tab => tab.tab_name === 'recorded')?.is_visible === 'true',
            community: response.is_visibility.find(tab => tab.tab_name === 'community')?.is_visible === 'true'
          };
          setTabVisibility(newTabVisibility);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to fetch dashboard data');
    }
  };

  useEffect(() => {
    fetchDashboardData();
    setLoading(false);
  }, []);

  useEffect(() => {
    // console.log('Classroom name state updated:', classroomName);
  }, [classroomName]);

  const handleTabChange = (tab) => {
    // Check permissions before changing tabs
    switch(tab) {
      case 'assignments':
        if (!permissions.classroom_assignment_view) {
          toast.warning("You don't have permission to view assignments");
          return;
        }
        break;
      case 'assessments':
        if (!permissions.classroom_assessment_view) {
          toast.warning("You don't have permission to view assessments");
          return;
        }
        break;
      case 'live':
        if (!permissions.classroom_live_classes_view) {
          toast.warning("You don't have permission to view live classes");
          return;
        }
        break;
      case 'recorded':
        if (!permissions.classroom_resources_view) {
          toast.warning("You don't have permission to view resources");
          return;
        }
        break;
      case 'community':
        if (!permissions.classroom_community_view) {
          toast.warning("You don't have permission to view community");
          return;
        }
        break;
    }
    setActiveTab(tab);
  };

  const handleAddTrainee = () => {
    if (!permissions.classroom_trainees_view) {
      toast.warning("You don't have permission to view or add trainees");
      return;
    }
    let encodedClassroomId = encodeData(decodedClassroomId);
    navigate(`/admin/live-sessions/classroom-dashboard/${encodedClassroomId}/trainees/${encodedClassroomId}`);
  };

  const toggleTrainers = () => {
    setIsTrainersExpanded(!isTrainersExpanded);
  };

  const handleBackToSessions = () => {
    navigate('/admin/live-sessions');
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden flex items-center justify-center min-h-[320px]">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#4F6BFF] border-t-transparent" aria-hidden />
          <span className="text-sm font-medium">Loading session…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      {/* Top: back + session name + primary action */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 bg-gray-50/50">
        <button
          type="button"
          onClick={handleBackToSessions}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm mb-4 sm:mb-0 sm:mr-4"
          title="Back to Live Sessions"
        >
          <Icon icon="fluent:arrow-left-24-regular" width="20" height="20" className="shrink-0" />
          <span>Back to Live Sessions</span>
        </button>
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mt-3 sm:mt-0">
          <h1 className="text-xl font-semibold text-gray-900 truncate m-0 flex-1 min-w-0">
            {classroomName || 'Live Session'}
          </h1>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={`inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  activeTab === 'live'
                    ? 'bg-[#4F6BFF] text-white shadow-md shadow-[#4F6BFF]/25'
                    : 'bg-white text-gray-600 hover:bg-white hover:text-[#4F6BFF] border border-gray-200 hover:border-[#4F6BFF]/40 shadow-sm'
                } ${!permissions.classroom_live_classes_view ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => handleTabChange('live')}
                disabled={!permissions.classroom_live_classes_view}
                title={!permissions.classroom_live_classes_view ? "You don't have permission to view live sessions" : "View Live Sessions"}
              >
                <Icon icon="fluent:video-person-24-regular" width="18" height="18" />
                Live Sessions
              </button>

              <button
                type="button"
                className={`inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  activeTab === 'assignments'
                    ? 'bg-[#4F6BFF] text-white shadow-md shadow-[#4F6BFF]/25'
                    : 'bg-white text-gray-600 hover:bg-white hover:text-[#4F6BFF] border border-gray-200 hover:border-[#4F6BFF]/40 shadow-sm'
                } ${!permissions.classroom_assignment_view ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => handleTabChange('assignments')}
                disabled={!permissions.classroom_assignment_view}
                title={!permissions.classroom_assignment_view ? "You don't have permission to view assignments" : "View Assignments"}
              >
                <Icon icon="fluent:clipboard-task-24-regular" width="18" height="18" />
                Assignments
              </button>

              <button
                type="button"
                className={`inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  activeTab === 'assessments'
                    ? 'bg-[#4F6BFF] text-white shadow-md shadow-[#4F6BFF]/25'
                    : 'bg-white text-gray-600 hover:bg-white hover:text-[#4F6BFF] border border-gray-200 hover:border-[#4F6BFF]/40 shadow-sm'
                } ${!permissions.classroom_assessment_view ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => handleTabChange('assessments')}
                disabled={!permissions.classroom_assessment_view}
                title={!permissions.classroom_assessment_view ? "You don't have permission to view assessments" : "View Assessments"}
              >
                <Icon icon="fluent:quiz-new-24-regular" width="18" height="18" />
                Assessments
              </button>
            </div>

            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              onClick={handleAddTrainee}
              disabled={!permissions.classroom_trainees_view}
              title={!permissions.classroom_trainees_view ? "You don't have permission to view or add trainees" : "Add Trainees"}
            >
              <Icon icon="fluent:people-add-24-regular" width="18" height="18" className="shrink-0" />
              <span>Add Trainees</span>
            </button>
          </div>
        </div>
      </div>

      {/* Trainers – collapsible card */}
      <div className="mx-4 sm:mx-6 mt-4">
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
          <button
            type="button"
            onClick={toggleTrainers}
            className="w-full flex justify-between items-center p-4 text-left hover:bg-gray-50/50 transition-colors"
          >
            <span className="flex items-center gap-2 font-medium text-gray-900">
              <Icon icon="fluent:people-team-24-regular" width="20" height="20" className="text-gray-500" />
              Classroom Trainers
              <span className="text-gray-500 font-normal">({trainers.length})</span>
            </span>
            <Icon
              icon={isTrainersExpanded ? 'fluent:chevron-up-24-regular' : 'fluent:chevron-down-24-regular'}
              width="20"
              height="20"
              className="text-gray-400"
            />
          </button>
          {isTrainersExpanded && (
            <div className="px-4 pb-4 pt-0 border-t border-gray-100">
              <div className="flex flex-wrap gap-3 pt-4">
                {trainers.length === 0 ? (
                  <p className="text-gray-500 text-sm w-full text-center py-4">No trainers added yet.</p>
                ) : (
                  trainers.map((trainer) => (
                    <div
                      key={trainer.user_id}
                      className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/30 min-w-0"
                    >
                      {trainer.profile_pic_url ? (
                        <img
                          src={trainer.profile_pic_url}
                          alt=""
                          className="rounded-full w-12 h-12 object-cover shrink-0"
                        />
                      ) : (
                        <div className="rounded-full w-12 h-12 bg-gray-200 flex items-center justify-center shrink-0">
                          <Icon icon="fluent:person-24-regular" width="24" height="24" className="text-gray-500" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate m-0">{trainer.name}</p>
                        <span className="text-xs text-[#4F6BFF] font-medium">{trainer.role}</span>
                        <div className="flex flex-col gap-0.5 mt-1">
                          <a href={`mailto:${trainer.email}`} className="text-sm text-gray-600 hover:text-[#4F6BFF] truncate no-underline">
                            {trainer.email}
                          </a>
                          {trainer.phone && (
                            <a href={`tel:${trainer.phone}`} className="text-sm text-gray-600 hover:text-[#4F6BFF] no-underline">
                              {trainer.phone}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content p-4 sm:p-5">
            {activeTab === 'assignments' && permissions.classroom_assignment_view && (
              <div className="assignments">
                <ClassroomAssignments decodedClassroomId={decodedClassroomId} />
              </div>
            )}

            {activeTab === 'assessments' && permissions.classroom_assessment_view && (
              <div className="assessments">
                <ClassroomAssessments decodedClassroomId={decodedClassroomId} />
              </div>
            )}

            {activeTab === 'live' && permissions.classroom_live_classes_view && (
              <div className="live-classes">
                <ClassroomLiveClasses decodedClassroomId={decodedClassroomId} />
              </div>
            )}

            {/* Hidden: Resources tab content
            {activeTab === 'recorded' && permissions.classroom_resources_view && (
              <div className="recorded">
                <ClassroomRecorded decodedClassroomId={decodedClassroomId} />
              </div>
            )}
            */}

            {/* Hidden: Community tab content
            {activeTab === 'community' && permissions.classroom_community_view && (
              <div className="community">
                <ClassroomCommunity
                  classroom_id={classroomData?.classroom_id}
                  group_id={groupData?.group_id}
                  group_name={groupData?.group_name}
                />
              </div>
            )}
            */}

            {/* Show message when user doesn't have permission for current tab */}
            {((activeTab === 'assignments' && !permissions.classroom_assignment_view) ||
              (activeTab === 'assessments' && !permissions.classroom_assessment_view) ||
              (activeTab === 'live' && !permissions.classroom_live_classes_view)) && (
              <div className="text-center py-5">
                <Icon icon="fluent:error-circle-24-regular" className="text-red-500 mb-3" width="48" height="48" />
                <h5>Access Denied</h5>
                <p className="text-gray-500">You don't have permission to view this content.</p>
              </div>
            )}
          </div>
    </div>
  )
}

export default ClassroomDashboard 